import { AlertTriangleIcon, ArrowLeftRightIcon, BoxesIcon, ScanLineIcon } from 'lucide-react'
import type { ProductPageContent } from '../../types/product'
import { industryCatalog, productCatalog } from './shared'

export const inventory: ProductPageContent = {
  slug: 'inventory',
  name: 'Inventory',
  icon: BoxesIcon,
  hero: {
    headline: 'Know what’s on your shelves, and what to order next',
    subheadline:
      'Track products and materials in real time across every shop and store, get alerts before you run out, and stop losing money to missing stock.',
    trustLine: 'Real-time stock across every location · Used by 6,500+ businesses',
    visual: {
      kind: 'devices',
      tablet: {
        module: 'Inventory',
        greeting: 'Stock overview · Remera branch',
        kpis: [
          { label: 'Stock value', value: 'RWF 18.4M', delta: { label: '+4.1% this month', tone: 'positive' } },
          { label: 'Products', value: '1,248' },
          { label: 'Low stock', value: '14 items', delta: { label: 'Reorder today', tone: 'warning' } },
        ],
        main: {
          type: 'progress',
          title: 'Stock levels',
          meta: 'Fast-moving items',
          rows: [
            { label: 'Maize flour 5kg', value: '3 left', pct: 8, tone: 'negative' },
            { label: 'Cooking oil 1L', value: '6 left', pct: 15, tone: 'warning' },
            { label: 'Rice 5kg', value: '48 left', pct: 70 },
            { label: 'Sugar 1kg', value: '64 left', pct: 85 },
          ],
        },
        side: {
          type: 'list',
          title: 'Moved today',
          rows: [
            { label: 'Sold', value: '312', tone: 'default' },
            { label: 'Received', value: '+180', tone: 'positive' },
            { label: 'Transferred', value: '40' },
            { label: 'Damaged', value: '-2', tone: 'negative' },
          ],
        },
      },
      phone: {
        type: 'list',
        title: 'Low stock alert',
        badge: { label: '2 items', tone: 'warning' },
        rows: [
          { label: 'Maize flour 5kg', sublabel: 'Remera', value: '3 left', tone: 'negative' },
          { label: 'Cooking oil 1L', sublabel: 'Kimironko', value: '6 left', tone: 'warning' },
        ],
        action: 'Create purchase order',
      },
    },
  },
  stats: [
    { value: '6,500+', label: 'businesses tracking stock with Kayko' },
    { value: '2.4M', label: 'products counted and tracked every month' },
    { value: '−38%', label: 'average drop in stock-outs after six months' },
  ],
  benefits: {
    heading: 'Stock you can count on.',
    items: [
      { icon: ScanLineIcon, title: 'Always accurate', copy: 'Every sale, delivery, and return updates your stock the moment it happens.' },
      { icon: AlertTriangleIcon, title: 'Never run out', copy: 'Set reorder levels for each product and get alerts before shelves go empty.' },
      { icon: ArrowLeftRightIcon, title: 'Every location', copy: 'See stock across shops, stores, and warehouses, and move it where it sells.' },
    ],
  },
  features: {
    heading: 'Everything you need to manage stock.',
    subheading: 'From a single shop to a warehouse supplying several branches.',
    items: [
      {
        title: 'Real-time stock levels',
        copy: 'See exactly how much of every product you have, at every location, right now. Stock updates automatically with each sale, delivery, and return.',
        bullets: ['Barcode and SKU support for every product', 'Variants such as size, colour, and pack', 'Stock value at cost and selling price'],
        visual: {
          kind: 'card',
          card: {
            type: 'list',
            title: 'Products · Groceries',
            meta: '248 items · RWF 6,820,000 at cost',
            rows: [
              { label: 'Rice 5kg', sublabel: 'SKU 60012 · Shelf B2', value: '48 units' },
              { label: 'Sugar 1kg', sublabel: 'SKU 60031 · Shelf B4', value: '64 units' },
              { label: 'Cooking oil 1L', sublabel: 'SKU 60044 · Shelf C1', value: '6 units', tone: 'warning' },
              { label: 'Maize flour 5kg', sublabel: 'SKU 60058 · Shelf A3', value: '3 units', tone: 'negative' },
            ],
          },
        },
      },
      {
        title: 'Low-stock alerts and reorder points',
        copy: 'Set a minimum level for each product and Kayko tells you when to reorder. Turn alerts into purchase orders in one tap.',
        bullets: ['Custom reorder levels per product and branch', 'Alerts by app notification or SMS', 'Suggested order quantities based on sales'],
        visual: {
          kind: 'card',
          card: {
            type: 'progress',
            title: 'Reorder soon',
            badge: { label: '4 items', tone: 'warning' },
            rows: [
              { label: 'Maize flour 5kg', value: '3 / 20', pct: 15, tone: 'negative' },
              { label: 'Cooking oil 1L', value: '6 / 24', pct: 25, tone: 'warning' },
              { label: 'Tea leaves 250g', value: '9 / 20', pct: 45, tone: 'warning' },
              { label: 'Soap bar', value: '22 / 40', pct: 55 },
            ],
            action: 'Order suggested quantities',
          },
        },
      },
      {
        title: 'Transfers between locations',
        copy: 'Move stock from your warehouse to your shops, or between branches, with a full record of who sent and received it. Nothing goes missing on the way.',
        bullets: ['Send and receive transfers from any device', 'Track items in transit', 'Confirm deliveries with the receiving staff'],
        visual: {
          kind: 'card',
          card: {
            type: 'list',
            title: 'Transfer #TR-0192',
            meta: 'Gikondo warehouse → Kimironko shop',
            badge: { label: 'In transit', tone: 'brand' },
            rows: [
              { label: 'Rice 5kg', value: '20 units' },
              { label: 'Cooking oil 1L', value: '36 units' },
              { label: 'Sugar 1kg', value: '40 units' },
            ],
            footer: { label: 'Sent by', value: 'Eric Habimana' },
            action: 'Confirm received',
          },
        },
      },
      {
        title: 'Stock counts and adjustments',
        copy: 'Run full or partial stock counts from your phone and see differences instantly. Record damaged, expired, or missing items with a reason.',
        bullets: ['Count by shelf, category, or supplier', 'Variance report after every count', 'Expiry dates for food and medicine'],
        visual: {
          kind: 'card',
          card: {
            type: 'list',
            title: 'Stock count · Aisle B',
            meta: 'Counted by Diane Mukamana · 18 Sep',
            rows: [
              { label: 'Rice 5kg', sublabel: 'Expected 48', value: '48', tone: 'positive' },
              { label: 'Milk 500ml', sublabel: 'Expected 90', value: '86', tone: 'negative' },
              { label: 'Bread loaf', sublabel: 'Expected 35 · 2 expired', value: '33', tone: 'warning' },
            ],
            footer: { label: 'Variance value', value: '−RWF 5,200' },
          },
        },
      },
    ],
  },
  steps: {
    heading: 'Up and running in an afternoon.',
    items: [
      { title: 'Import your products', copy: 'Upload your product list from a spreadsheet or add items with prices and barcodes.' },
      { title: 'Set opening stock', copy: 'Enter current quantities per location, or run a quick first count with your team.' },
      { title: 'Let it track itself', copy: 'Sales, deliveries, and transfers keep your stock accurate from day one.' },
    ],
  },
  related: {
    heading: 'Works with the rest of Kayko.',
    subheading: 'Stock stays connected to what you sell, buy, and report.',
    items: [productCatalog['sales-pos'], productCatalog.purchases, productCatalog.reports, productCatalog['business-finance']],
  },
  industries: {
    heading: 'Built for your industry.',
    subheading: 'Inventory adapts to products, ingredients, materials, and medicine.',
    items: [industryCatalog.retail, industryCatalog.food, industryCatalog.manufacturing, industryCatalog.healthcare],
  },
  testimonial: {
    quote:
      'I used to find out I was out of stock when a customer asked. Now Kayko tells me before it happens, and I order exactly what sells.',
    name: 'Jemima Nyirasafari',
    role: 'Owner',
    business: 'All-in-one supermarket',
    location: 'Kicukiro, Kigali',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/26e15333-45ec-4421-ac04-24cf98518008.jpg',
    metric: { value: '−45%', label: 'fewer stock-outs in her first four months' },
  },
  pricingTeaser: { text: 'Inventory is included in every Kayko plan.', linkLabel: 'See pricing', href: '#' },
  faq: {
    heading: 'Frequently asked questions',
    items: [
      { question: 'Do I need a barcode scanner?', answer: 'No. You can search products by name or scan barcodes with your phone camera. Bluetooth and USB scanners also work if you prefer.' },
      { question: 'Can I track stock across several shops?', answer: 'Yes. Each shop, store, or warehouse has its own stock levels, and you can see them together or one at a time.' },
      { question: 'Does it work offline?', answer: 'Yes. Stock changes are saved on your device and sync as soon as you reconnect.' },
      { question: 'Can I track raw materials and ingredients?', answer: 'Yes. Track materials in any unit — kilograms, litres, metres — and link them to the products you make or sell.' },
      { question: 'How do I handle expired or damaged goods?', answer: 'Record an adjustment with a reason. Kayko keeps a full history so you can see losses by product and period.' },
      { question: 'Can I import my current stock list?', answer: 'Yes. Upload a spreadsheet with products, prices, and quantities, and our Kigali team can help you prepare it.' },
    ],
  },
  cta: {
    headline: 'Take control of your stock today.',
    subheadline: 'Set up your products in minutes, with support from our team in Kigali.',
  },
}
